import { useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useSession } from "@pontimed/auth-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { KeyRound, Search, ShieldCheck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { ALL_ADMIN_PERMISSIONS, type AdminPermission } from "@/lib/permissions";

/**
 * Admin → Admin permissions management (Phase 8c of D10/D11 in
 * `_docs/web-portal-navigation.md`).
 *
 * Superadmin-only — search any user by email or name, open a per-user
 * editor sheet, and grant/revoke individual `AdminPermission` rows. The
 * UI mirrors the catalog in `apps/admin/src/lib/permissions.ts`.
 *
 * Backend: /api/admin/permissions/* (apps/api/src/routes/admin-permissions.ts).
 *
 * Self-edit guard: the `userId === currentUser.id` case is locked client-side
 * (toggles disabled + tip) AND server-side (403 from the API) so a stale UI
 * cannot accidentally lock the requester out of their own scopes.
 */

export const Route = createFileRoute("/_authenticated/users-permissions")({
  component: UsersPermissionsPage,
});

interface UserSearchResult {
  id: string;
  email: string | null;
  name: string | null;
  isSuperadmin: boolean;
  adminPermissionsCount: number;
}

interface UserPermissionsRow {
  permission: string;
  grantedAt: string;
  grantedBy: { id: string; email: string | null } | null;
}

interface UserPermissionsPayload {
  user: {
    id: string;
    email: string | null;
    name: string | null;
    isSuperadmin: boolean;
  };
  permissions: UserPermissionsRow[];
}

// Human-readable labels for each granular permission. Keep in sync with
// _docs/web-portal-navigation.md D10 (Admin Surface table).
const PERMISSION_LABELS: Record<AdminPermission, { label: string; description: string }> = {
  "admin:dsa": {
    label: "DSA reports",
    description: "Handle Digital Services Act notice & action queue.",
  },
  "admin:feedback": {
    label: "Feedback triage",
    description: "Read and act on user feedback submissions.",
  },
  "admin:billing": {
    label: "Billing & overages",
    description: "View invoices, manage subscription overages and add-ons.",
  },
  "admin:countries": {
    label: "Countries",
    description: "Configure country status, languages, payment, OTP channel.",
  },
  "admin:marketing": {
    label: "Marketing",
    description: "Campaigns, journeys, leads and marketing settings.",
  },
  "admin:businesses": {
    label: "Businesses & facilities",
    description: "Brand-level businesses (pharmacy/lab/imaging) and their facilities.",
  },
  "admin:notifications": {
    label: "Notifications",
    description: "View realtime admin event feed and notification settings.",
  },
  "admin:audit": {
    label: "Audit logs",
    description: "Read the GDPR / NIS2 audit trail across the platform.",
  },
  "admin:challenges": {
    label: "Challenges",
    description: "Moderate community challenges and votes.",
  },
  "admin:features": {
    label: "Features & analytics",
    description: "Feature catalog + per-tier analytics and overage policies.",
  },
  "admin:compliance": {
    label: "2FA compliance",
    description: "Enforce mandatory 2FA across staff/providers.",
  },
  "admin:claims": {
    label: "Claims",
    description: "Review claim requests for facilities and businesses.",
  },
  "admin:providers": {
    label: "Provider approval",
    description: "Approve, reject, suspend and re-verify doctor profiles.",
  },
};

function UsersPermissionsPage() {
  const { user: currentUser } = useSession();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Defense-in-depth: even though the route is reachable only from the
  // sidebar (filtered by hasPermission and Dashboard fallback), a typed-URL
  // attempt by a granular admin should hit a 403 panel rather than the page.
  if (!currentUser?.isSuperadmin) {
    return <ForbiddenPanel />;
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-foreground flex items-center gap-2 text-2xl font-bold tracking-tight">
          <KeyRound className="text-muted-foreground size-6" aria-hidden />
          <Trans>Admin permissions management</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>
            Search any user by email or name, then grant or revoke individual admin permissions.
            Superadmin status is managed separately via the user record.
          </Trans>
        </p>
      </header>

      <UserSearch
        searchQuery={searchQuery}
        debouncedSearch={debouncedSearch}
        onSearchChange={setSearchQuery}
        onOpen={(userId) => setSelectedUserId(userId)}
      />

      <PermissionEditorSheet
        userId={selectedUserId}
        currentUserId={currentUser.userId}
        onClose={() => setSelectedUserId(null)}
      />

      <Card>
        <CardContent className="text-muted-foreground p-4 text-xs">
          <p className="mb-1">
            <strong className="text-foreground">
              <Trans>Note:</Trans>
            </strong>{" "}
            <Trans>
              Permission changes propagate to a logged-in admin within ~15 minutes (next access
              token refresh) or on next login. The catalog above mirrors
            </Trans>{" "}
            <code className="bg-muted text-foreground rounded px-1 py-0.5 font-mono">
              apps/admin/src/lib/permissions.ts
            </code>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// User search
// ---------------------------------------------------------------------------

interface UserSearchProps {
  searchQuery: string;
  debouncedSearch: string;
  onSearchChange: (q: string) => void;
  onOpen: (userId: string) => void;
}

function UserSearch({ searchQuery, debouncedSearch, onSearchChange, onOpen }: UserSearchProps) {
  const { t } = useLingui();
  const trimmed = debouncedSearch.trim();
  const enabled = trimmed.length >= 2;

  const query = useQuery({
    queryKey: ["admin", "permissions", "users", "search", trimmed],
    enabled,
    queryFn: async (): Promise<UserSearchResult[]> => {
      try {
        const data = await apiFetch.get<{ users: UserSearchResult[] }>(
          `/api/admin/permissions/users/search?q=${encodeURIComponent(trimmed)}`,
        );
        return data.users ?? [];
      } catch (e) {
        if (e instanceof ApiError) return [];
        throw e;
      }
    },
    staleTime: 30_000,
  });

  const users = query.data ?? [];

  return (
    <div className="space-y-3">
      <div className="relative max-w-md">
        <Search
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
          aria-hidden
        />
        <Input
          type="search"
          placeholder={t`Search by email or name…`}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
          aria-label={t`Search users`}
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Trans>Email</Trans>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    <Trans>Name</Trans>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Trans>Status</Trans>
                  </TableHead>
                  <TableHead>
                    <Trans>Permissions</Trans>
                  </TableHead>
                  <TableHead className="text-right">
                    <Trans>Actions</Trans>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!enabled ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                      <Trans>Type at least 2 characters to search.</Trans>
                    </TableCell>
                  </TableRow>
                ) : query.isLoading ? (
                  <UserSearchSkeleton />
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                      <Trans>No users match the query.</Trans>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.email ?? "—"}</TableCell>
                      <TableCell className="hidden md:table-cell">{u.name ?? "—"}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {u.isSuperadmin ? (
                          <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-200 dark:bg-amber-950 dark:text-amber-300">
                            <ShieldCheck className="mr-1 size-3" aria-hidden />
                            <Trans>Superadmin</Trans>
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            <Trans>Staff</Trans>
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">{u.adminPermissionsCount}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onOpen(u.id)}
                          className="min-h-11"
                        >
                          <Trans>Manage</Trans>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function UserSearchSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-4 w-40" />
          </TableCell>
          <TableCell className="hidden md:table-cell">
            <Skeleton className="h-4 w-32" />
          </TableCell>
          <TableCell className="hidden lg:table-cell">
            <Skeleton className="h-5 w-20 rounded-full" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-6" />
          </TableCell>
          <TableCell className="text-right">
            <Skeleton className="ml-auto h-9 w-20" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Per-user permission editor sheet
// ---------------------------------------------------------------------------

interface PermissionEditorSheetProps {
  userId: string | null;
  currentUserId: string;
  onClose: () => void;
}

function PermissionEditorSheet({ userId, currentUserId, onClose }: PermissionEditorSheetProps) {
  const open = userId !== null;
  const queryClient = useQueryClient();
  const isSelfEdit = userId === currentUserId;

  const detailQuery = useQuery({
    queryKey: ["admin", "permissions", "users", userId],
    enabled: open,
    queryFn: async (): Promise<UserPermissionsPayload | null> => {
      if (!userId) return null;
      try {
        return await apiFetch.get<UserPermissionsPayload>(`/api/admin/permissions/users/${userId}`);
      } catch (e) {
        if (e instanceof ApiError) return null;
        throw e;
      }
    },
    staleTime: 0,
  });

  const grantedSet = useMemo(() => {
    const s = new Set<string>();
    for (const r of detailQuery.data?.permissions ?? []) s.add(r.permission);
    return s;
  }, [detailQuery.data]);

  const grantMutation = useMutation({
    mutationFn: async (permission: AdminPermission) => {
      if (!userId) throw new Error("missing userId");
      return apiFetch.post(`/api/admin/permissions/users/${userId}`, { permission });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "permissions", "users", userId] });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "permissions", "users", "search"],
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (permission: AdminPermission) => {
      if (!userId) throw new Error("missing userId");
      return apiFetch.delete(
        `/api/admin/permissions/users/${userId}/${encodeURIComponent(permission)}`,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "permissions", "users", userId] });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "permissions", "users", "search"],
      });
    },
  });

  const togglePermission = (permission: AdminPermission, currentlyOn: boolean) => {
    if (isSelfEdit) return;
    if (currentlyOn) {
      revokeMutation.mutate(permission);
    } else {
      grantMutation.mutate(permission);
    }
  };

  const lastGrant = useMemo(() => {
    const rows = detailQuery.data?.permissions ?? [];
    if (rows.length === 0) return null;
    return [...rows].sort(
      (a, b) => new Date(b.grantedAt).getTime() - new Date(a.grantedAt).getTime(),
    )[0];
  }, [detailQuery.data]);

  const errorMessage = grantMutation.error || revokeMutation.error;

  return (
    <Sheet open={open} onOpenChange={(next) => (next ? null : onClose())}>
      <SheetContent side="right" className="w-full max-w-lg sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>
            <Trans>Manage admin permissions</Trans>
          </SheetTitle>
          <SheetDescription>
            <Trans>Toggle individual permissions for this user.</Trans>
          </SheetDescription>
        </SheetHeader>

        {detailQuery.isLoading ? (
          <PermissionEditorSkeleton />
        ) : !detailQuery.data ? (
          <div className="text-muted-foreground mt-6 text-sm">
            <Trans>Could not load user permissions. Try closing and re-opening this panel.</Trans>
          </div>
        ) : (
          <div className="mt-4 flex flex-col gap-5">
            <UserHeader user={detailQuery.data.user} />

            {detailQuery.data.user.isSuperadmin && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
                <Trans>
                  Superadmin has every permission implicitly. Granting or revoking individual rows
                  here has no effect on access — but rows persist if added.
                </Trans>
              </div>
            )}

            {isSelfEdit && (
              <div className="border-border bg-muted text-muted-foreground rounded-md border px-3 py-2 text-xs">
                <Trans>You cannot edit your own permissions.</Trans>
              </div>
            )}

            {errorMessage && (
              <div className="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-3 py-2 text-xs">
                <Trans>Action failed. Try again.</Trans>
              </div>
            )}

            <ul className="border-border divide-border divide-y rounded-md border">
              {ALL_ADMIN_PERMISSIONS.map((perm) => {
                const meta = PERMISSION_LABELS[perm];
                const on = grantedSet.has(perm);
                const pending =
                  (grantMutation.isPending && grantMutation.variables === perm) ||
                  (revokeMutation.isPending && revokeMutation.variables === perm);
                return (
                  <li key={perm} className="flex items-center justify-between gap-3 px-3 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-foreground text-sm font-medium">{meta.label}</div>
                      <div className="text-muted-foreground text-xs">{meta.description}</div>
                      <code className="text-muted-foreground/80 mt-1 inline-block font-mono text-[10px]">
                        {perm}
                      </code>
                    </div>
                    <Switch
                      checked={on}
                      onCheckedChange={() => togglePermission(perm, on)}
                      disabled={isSelfEdit || pending}
                      aria-label={meta.label}
                    />
                  </li>
                );
              })}
            </ul>

            <div className="border-border text-muted-foreground border-t pt-3 text-xs">
              {lastGrant ? (
                <Trans>
                  Last grant: {new Date(lastGrant.grantedAt).toLocaleString()}
                  {lastGrant.grantedBy?.email ? ` · by ${lastGrant.grantedBy.email}` : ""}
                </Trans>
              ) : (
                <Trans>No permissions granted yet.</Trans>
              )}
            </div>

            <div className="flex justify-end">
              <Button variant="outline" onClick={onClose} className="min-h-11">
                <Trans>Close</Trans>
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function UserHeader({ user }: { user: UserPermissionsPayload["user"] }) {
  return (
    <div className="border-border bg-card flex flex-col gap-1 rounded-md border px-3 py-2.5">
      <div className="text-foreground text-sm font-medium">{user.email ?? "—"}</div>
      {user.name && <div className="text-muted-foreground text-xs">{user.name}</div>}
      <div className="mt-1 flex flex-wrap gap-1.5">
        {user.isSuperadmin ? (
          <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-200 dark:bg-amber-950 dark:text-amber-300">
            <ShieldCheck className="mr-1 size-3" aria-hidden />
            <Trans>Superadmin</Trans>
          </Badge>
        ) : (
          <Badge variant="outline">
            <Trans>Staff</Trans>
          </Badge>
        )}
      </div>
    </div>
  );
}

function PermissionEditorSkeleton() {
  return (
    <div className="mt-4 flex flex-col gap-3">
      <Skeleton className="h-12 w-full rounded-md" />
      <ul className="border-border divide-border divide-y rounded-md border">
        {Array.from({ length: 6 }).map((_, i) => (
          <li key={i} className="flex items-center justify-between gap-3 px-3 py-3">
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
            <Skeleton className="size-8 rounded-full" />
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 403 panel — typed-URL access by a non-superadmin granular admin.
// ---------------------------------------------------------------------------

function ForbiddenPanel() {
  return (
    <div className="bg-card border-border mx-auto mt-12 flex max-w-lg flex-col items-center gap-3 rounded-lg border px-6 py-10 text-center">
      <ShieldCheck className="text-muted-foreground size-8" aria-hidden />
      <h2 className="text-foreground text-xl font-semibold">
        <Trans>Superadmin only</Trans>
      </h2>
      <p className="text-muted-foreground text-sm">
        <Trans>
          Granting and revoking admin permissions is restricted to superadmins. If you need a
          permission, ask a superadmin to grant it.
        </Trans>
      </p>
    </div>
  );
}
